import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Send, Sparkles, Loader2, Bot } from 'lucide-react';
import MDEditor from '@uiw/react-md-editor';
import { useEffect, useRef } from 'react';
import { cn } from '../lib/utils';
import { Card } from '../components/ui/card';
import { useApp } from '../context/AppContext';

export function ChatbotScreen() {
    // Use Chat from global context for persistence
    const { chat } = useApp();
    const { messages, input, setInput, sendMessage, isLoading } = chat;

    const bottomRef = useRef<HTMLDivElement>(null);

    const handleSend = () => {
        if (!input.trim() || isLoading) return;
        sendMessage(input);
        setInput(''); // Clear input
    };

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    return (
        <div className="flex flex-col h-full bg-slate-50 relative">
            <div className="bg-white/80 backdrop-blur-md p-3 text-center text-xs text-muted-foreground border-b sticky top-0 z-10">
                Ask about users, subscriptions, or revenue. I know your current context!
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-6">
                {messages.length === 0 && (
                    <div className="flex flex-col items-center justify-center h-full opacity-50 space-y-6">
                        <div className="h-16 w-16 bg-white rounded-2xl shadow-sm border flex items-center justify-center">
                            <Sparkles className="h-8 w-8 text-primary" />
                        </div>
                        <p className="text-sm font-medium text-muted-foreground">How can I help you manage the library today?</p>
                        <div className="grid grid-cols-2 gap-3 w-full max-w-sm">
                            <Button variant="outline" className="h-auto py-3 px-4 text-xs justify-start bg-white hover:bg-slate-50" onClick={() => sendMessage("Show me total revenue for this month")}>
                                <span className="mr-2">💰</span> Revenue this month
                            </Button>
                            <Button variant="outline" className="h-auto py-3 px-4 text-xs justify-start bg-white hover:bg-slate-50" onClick={() => sendMessage("Who subscribed recently?")}>
                                <span className="mr-2">👥</span> Recent subs
                            </Button>
                        </div>
                    </div>
                )}

                {messages.map((m: any, i: number) => {
                    const isUser = m.role === 'user';
                    const text = m.parts
                        .filter((p: any) => p.type === 'text')
                        .map((p: any) => p.content).join('');

                    // Check for tool calls
                    const toolCalls = m.parts.filter((p: any) => p.type === 'tool-call');

                    return (
                        <div key={i} className={cn("flex w-full animate-in fade-in slide-in-from-bottom-2 duration-300", isUser ? "justify-end" : "justify-start")}>
                            <div className={cn(
                                "flex gap-3 max-w-[85%]",
                                isUser ? "flex-row-reverse" : "flex-row"
                            )}>
                                {!isUser && (
                                    <div className="h-8 w-8 shrink-0 rounded-full bg-white border shadow-sm flex items-center justify-center mt-1">
                                        <Bot className="h-4 w-4 text-primary" />
                                    </div>
                                )}

                                <div className="space-y-2 w-full">
                                    {/* Text Content */}
                                    {text && (
                                        <div className={cn(
                                            "px-4 py-3 text-sm shadow-sm",
                                            isUser
                                                ? "bg-primary text-primary-foreground rounded-2xl rounded-tr-sm"
                                                : "bg-white border text-foreground rounded-2xl rounded-tl-sm"
                                        )}>
                                            {isUser ? (
                                                <div className="whitespace-pre-wrap">{text}</div>
                                            ) : (
                                                <div className="markdown-body">
                                                    <MDEditor.Markdown
                                                        source={text}
                                                        style={{ background: 'transparent', color: 'inherit', fontSize: 'inherit', lineHeight: '1.5' }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Tool Execution Markers */}
                                    {!isUser && toolCalls.length > 0 && (
                                        <div className="space-y-2">
                                            {toolCalls.map((tc: any, idx: number) => {
                                                // If we have output/result, we may not need to show 'Calling...' or show it as done
                                                // TanStack AI doesn't easily link tool-call to tool-result in parts list without ID matching
                                                // But usually if the text response is generated, the tool is done.
                                                // Simple heuristic: if this message has text content coming AFTER this tool call, it's done. 
                                                // Or just showing it as a subtle log.

                                                return (
                                                    <div key={idx} className="flex items-center gap-2 text-xs text-muted-foreground ml-1">
                                                        <div className="h-4 w-4 rounded-full bg-green-100 flex items-center justify-center">
                                                            <div className="h-1.5 w-1.5 rounded-full bg-green-500" />
                                                        </div>
                                                        <span className="font-mono opacity-70">Used {tc.name}</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
                {isLoading && (
                    <div className="flex justify-start ml-11">
                        <div className="bg-white border px-4 py-3 rounded-2xl rounded-tl-sm shadow-sm flex gap-1 items-center">
                            <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce [animation-delay:-0.3s]"></span>
                            <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce [animation-delay:-0.15s]"></span>
                            <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce"></span>
                        </div>
                    </div>
                )}
                <div ref={bottomRef} className="h-1" />
            </div>

            <div className="p-4 bg-white border-t mb-8">
                <div className="relative flex items-center">
                    <Input
                        placeholder="Ask a question..."
                        className="pr-12 py-6 bg-slate-50 border-slate-200 focus-visible:ring-offset-0 focus-visible:ring-1"
                        value={input}
                        onChange={e => setInput(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
                        disabled={isLoading}
                    />
                    <Button
                        size="icon"
                        className="absolute right-1.5 h-9 w-9"
                        onClick={handleSend}
                        disabled={isLoading || !input.trim()}
                    >
                        <Send className="h-4 w-4" />
                    </Button>
                </div>
            </div>
        </div>
    );
}
